import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, Phone, Crown, Calendar, RefreshCw, ChevronDown, ChevronUp, ExternalLink, AlertTriangle, Printer, ShieldCheck } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/app/_module/components/ui/card";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Skeleton } from "@/app/_module/components/ui/skeleton";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/app/_module/components/ui/collapsible";
import { getApiUrl } from "@/app/_module/services/config";
import { VerifyPurchasesResult, type VerifyPurchasesData } from "./VerifyPurchasesResult";
import { endpoint } from "@/app/_module/services/endpoints";
import { getSupabaseSession } from "@/app/_module/services/auth";
import { useToast } from "@/app/_module/components/ui/use-toast";
import { isDemoMode } from "@/app/_module/utils/demoMode";

interface RecurringCharge {
  id: string;
  type: "subscription" | "area_code" | "fax_number";
  name: string;
  description: string;
  amount: number;
  currency: string;
  interval: string;
  status: string;
  nextBillingDate?: string | null;
  lastRenewedAt?: string | null;
  startDate?: string | null;
  autoRenew?: boolean;
  isFree?: boolean;
  freeTrialExpiresAt?: string | null;
}

interface RecurringChargesSummary {
  totalMonthly: number;
  subscriptionCount: number;
  paidAreaCodeCount: number;
  freeAreaCodeCount: number;
  faxNumberCount?: number;
  currency: string;
}

interface RecurringChargesResponse {
  charges: RecurringCharge[];
  summary: RecurringChargesSummary;
}

async function fetchRecurringCharges(): Promise<RecurringChargesResponse> {
  const session = await getSupabaseSession();
  const response = await fetch(
    getApiUrl(endpoint.subscriptions.getRecurringCharges),
    {
      headers: {
        Authorization: `Bearer ${session?.access_token}`,
        "Content-Type": "application/json",
      },
    }
  );

  if (!response.ok) {
    throw new Error("Failed to fetch recurring charges");
  }

  const result = await response.json();
  return result.data;
}

function formatCurrency(amount: number, currency: string = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(amount);
}

function StatusBadge({ status, isFree }: { status: string; isFree?: boolean }) {
  if (isFree) {
    return (
      <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
        Free Trial
      </Badge>
    );
  }

  const statusConfig: Record<string, { className: string; label: string }> = {
    active: { className: "bg-green-50 text-green-700 border-green-200", label: "Active" },
    trialing: { className: "bg-blue-50 text-blue-700 border-blue-200", label: "Trial" },
    past_due: { className: "bg-red-50 text-red-700 border-red-200", label: "Past Due" },
    canceled: { className: "bg-gray-50 text-gray-600 border-gray-200", label: "Canceled" },
    expired: { className: "bg-gray-50 text-gray-600 border-gray-200", label: "Expired" },
  };

  const config = statusConfig[status.toLowerCase()] || statusConfig.active;

  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
}

function ChargeItem({ charge }: { charge: RecurringCharge }) {
  const isSubscription = charge.type === "subscription";
  const isFaxNumber = charge.type === "fax_number";
  const Icon = isSubscription ? Crown : isFaxNumber ? Printer : Phone;
  const bgColor = isSubscription ? "bg-purple-100" : isFaxNumber ? "bg-green-100" : "bg-blue-100";
  const iconColor = isSubscription ? "text-purple-600" : isFaxNumber ? "text-green-600" : "text-blue-600";

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${bgColor}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{charge.name}</span>
            <StatusBadge status={charge.status} isFree={charge.isFree} />
          </div>
          <div className="text-sm text-gray-500">
            {charge.isFree && charge.freeTrialExpiresAt ? (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertTriangle className="w-3 h-3" />
                Expires {charge.freeTrialExpiresAt}
              </span>
            ) : charge.nextBillingDate ? (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Next billing: {charge.nextBillingDate}
              </span>
            ) : charge.startDate ? (
              <span>Started: {charge.startDate}</span>
            ) : null}
          </div>
        </div>
      </div>
      <div className="text-right">
        <div className="font-semibold text-gray-900">
          {charge.isFree ? (
            <span className="text-green-600">FREE</span>
          ) : (
            formatCurrency(charge.amount, charge.currency)
          )}
        </div>
        {!charge.isFree && <div className="text-xs text-gray-500">/{charge.interval}</div>}
      </div>
    </div>
  );
}

export function RecurringCharges() {
  const [verifyResult, setVerifyResult] = useState<VerifyPurchasesData | null>(null);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [isAreaCodesOpen, setIsAreaCodesOpen] = useState(true);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);
  const { toast } = useToast();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["recurring-charges"],
    queryFn: fetchRecurringCharges,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const handleManageSubscription = async () => {
    setIsOpeningPortal(true);
    try {
      const session = await getSupabaseSession();
      if (!session?.access_token) {
        toast({
          variant: "destructive",
          title: "Authentication Required",
          description: "Please sign in to manage your subscription.",
        });
        return;
      }

      const response = await fetch(getApiUrl("/stripe/create-portal-session"), {
        method: "POST",
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to create portal session");
      }

      const result = await response.json();
      if (result.data?.url) {
        sessionStorage.setItem("billingPortalOpened", "true");
        window.open(result.data.url, "_blank");
      } else {
        throw new Error("No portal URL received");
      }
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to open billing portal. Please try again.",
      });
    } finally {
      setIsOpeningPortal(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Recurring Charges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5" />
            Recurring Charges
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6">
            <p className="text-red-500 mb-4">Failed to load recurring charges</p>
            <Button variant="outline" onClick={() => refetch()}>
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const subscriptionCharges = data?.charges.filter((c) => c.type === "subscription") || [];
  // Only show paid area codes (exclude free trial area codes)
  const paidAreaCodeCharges = data?.charges.filter((c) => c.type === "area_code" && !c.isFree) || [];
  const faxNumberCharges = data?.charges.filter((c) => c.type === "fax_number") || [];
  const summary = data?.summary;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-blue-600" />
              Recurring Charges
            </CardTitle>
            <CardDescription>
              Your monthly subscription, area code, and fax billing
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => {
                // DEMO SANDBOX: this hits the LIVE Stripe account — it re-reads real checkout
                // sessions and can provision or cancel numbers off the back of them.
                if (isDemoMode()) {
                  window.dispatchEvent(
                    new CustomEvent('demo-restricted', {
                      detail:
                        "Verifying purchases is disabled in the demo sandbox. Start a free trial to manage real billing.",
                    }),
                  );
                  return;
                }

                try {
                  const session = await getSupabaseSession();
                  const res = await fetch(getApiUrl(endpoint.stripe.verifyPurchases), {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
                    body: '{}',
                  });
                  const json = await res.json();
                  const data = json?.data;
                  // Toast stays for the at-a-glance result; the panel carries the
                  // detail (what was added, what was skipped and why) which a
                  // toast has never had room for.
                  setVerifyError(null);
                  setVerifyResult(data ?? null);
                  setVerifyOpen(true);
                  if (data?.provisioned?.length > 0 || data?.subscriptionFix?.fixed || data?.duplicateSubscriptions?.length > 0) {
                    toast({ title: "Issues Found & Fixed", description: data.summary });
                  } else if (data?.webhookHealth?.reEnabled) {
                    toast({ title: "Webhook Restored", description: "Payment processing was restored." });
                  } else {
                    toast({ title: "All Good", description: data?.summary || "All purchases verified." });
                  }
                } catch {
                  setVerifyResult(null);
                  setVerifyError("We could not reach the billing service. Please try again in a moment.");
                  setVerifyOpen(true);
                  toast({ title: "Verification Failed", description: "Please try again.", variant: "destructive" });
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md border border-blue-200 transition-colors"
              title="Verify all purchases match your account"
            >
              <ShieldCheck className="h-4 w-4" />
              Verify Purchases
            </button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManageSubscription}
              disabled={isOpeningPortal}
              className="flex items-center gap-2"
            >
              <ExternalLink className="w-4 h-4" />
              {isOpeningPortal ? "Opening..." : "Manage Billing"}
            </Button>
          </div>
        </div>
        {summary && (
          <div className="flex items-center gap-2 mt-3 pt-3 border-t">
            <DollarSign className="h-5 w-5 text-green-600" />
            <span className="text-2xl font-bold text-gray-900">
              {formatCurrency(summary.totalMonthly, summary.currency)}
            </span>
            <span className="text-sm text-gray-500">/month total</span>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="text-center">
              <div className="text-lg font-semibold text-purple-600">
                {summary.subscriptionCount}
              </div>
              <div className="text-xs text-gray-500">Subscription</div>
            </div>
            <div className="text-center border-l border-gray-200">
              <div className="text-lg font-semibold text-blue-600">
                {summary.paidAreaCodeCount}
              </div>
              <div className="text-xs text-gray-500">Paid Area Codes</div>
            </div>
            <div className="text-center border-l border-gray-200">
              <div className="text-lg font-semibold text-green-600">
                {summary.faxNumberCount || 0}
              </div>
              <div className="text-xs text-gray-500">Fax Numbers</div>
            </div>
          </div>
        )}

        {/* Subscription Section */}
        {subscriptionCharges.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Crown className="w-4 h-4 text-purple-500" />
              Subscription
            </h3>
            {subscriptionCharges.map((charge) => (
              <ChargeItem key={charge.id} charge={charge} />
            ))}
          </div>
        )}

        {/* Paid Area Codes Section */}
        {paidAreaCodeCharges.length > 0 && (
          <Collapsible open={isAreaCodesOpen} onOpenChange={setIsAreaCodesOpen}>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between p-0 h-auto hover:bg-transparent"
              >
                <h3 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-500" />
                  Paid Area Codes ({paidAreaCodeCharges.length})
                </h3>
                {isAreaCodesOpen ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2">
              {/* Capped + scrolled in place: one row per area code, and accounts in the
                  70s exist — an uncapped list pushes the total, the Verify button and
                  everything below it far off-screen. max-h only engages once the list
                  is actually long, so short lists render exactly as before. */}
              <div className="max-h-80 overflow-y-auto overscroll-contain pr-1 -mr-1">
                {paidAreaCodeCharges.map((charge) => (
                  <ChargeItem key={charge.id} charge={charge} />
                ))}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Fax Numbers Section */}
        {faxNumberCharges.length > 0 && (
          <div className="mb-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <Printer className="w-4 h-4 text-green-500" />
              Fax Numbers ({faxNumberCharges.length})
            </h3>
            <div className="max-h-80 overflow-y-auto overscroll-contain pr-1 -mr-1">
              {faxNumberCharges.map((charge) => (
                <ChargeItem key={charge.id} charge={charge} />
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {!data?.charges.length && (
          <div className="text-center py-8 text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No recurring charges found</p>
            <p className="text-sm">Subscribe to a plan to get started</p>
          </div>
        )}
      </CardContent>

      <VerifyPurchasesResult
        open={verifyOpen}
        onOpenChange={setVerifyOpen}
        data={verifyResult}
        error={verifyError}
      />
    </Card>
  );
}
